import React, { useState, useEffect } from 'react'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { cn } from '@/lib/utils'

interface DashboardLayoutProps {
  children: React.ReactNode
  className?: string
}

export function DashboardLayout({ children, className }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check localStorage for persisted preference
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('sidebar-collapsed')
      return stored === 'true'
    }
    return false
  })

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [])

  return (
    <div className="app-shell min-h-screen bg-neutral-50 dark:bg-neutral-950 transition-colors duration-200">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 transition-all duration-200 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarCollapsed ? 'lg:w-[60px]' : 'lg:w-[240px]'
        )}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          onClose={() => setSidebarOpen(false)}
        />
      </aside>

      {/* Main content wrapper */}
      <div
        className={cn(
          'min-h-screen transition-all duration-200 ease-in-out',
          sidebarCollapsed ? 'lg:pl-[60px]' : 'lg:pl-[240px]'
        )}
      >
        {/* Header */}
        <Header
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarCollapsed={sidebarCollapsed}
          onSidebarCollapsedChange={setSidebarCollapsed}
        />

        {/* Page content */}
        <main className={cn('px-4 py-6 lg:px-6', className)}>
          {children}
        </main>
      </div>
    </div>
  )
}
